import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { cn } from "@/lib/utils";
import { DotPattern } from "@/components/ui/dot-pattern";
import { HugeiconsIcon } from "@hugeicons/react";
import { ClaudeIcon, GoogleGeminiIcon } from "@hugeicons/core-free-icons";
import sceneImage from "@/assets/showcase/image.jpeg";
import showcaseVideo from "@/assets/showcase/video.mp4";
import elevenlabsImg from "@/assets/landing/elevenlabs.png";

// ─── Feature flag ─────────────────────────────────────────────────
// Toggle between V1 (original) and V2 (redesign) node style.
const USE_V2_NODES = true;

// ─── Types & Data ────────────────────────────────────────────────

type NodeCategory = "input" | "generation" | "transform" | "output";

interface MockNodeData {
  id: string;
  label: string;
  category: NodeCategory;
  icon: React.ReactNode;
  /** Center-x position as % of canvas width */
  x: number;
  /** Center-y position as % of canvas height (node is centered via translateY(-50%)) */
  y: number;
  /** What to render in the body */
  content: "text-snippet" | "bars" | "image" | "waveform" | "video";
}

interface ConnectionData {
  from: string;
  to: string;
  color: string;
}

// ── Shared icon colors ────────────────────────────────────────────

const CATEGORY_ICON: Record<NodeCategory, string> = {
  input: "text-blue-600 dark:text-blue-400",
  generation: "text-[oklch(0.55_0.25_285)] dark:text-[oklch(0.65_0.22_285)]",
  transform: "text-emerald-600 dark:text-emerald-400",
  output: "text-amber-600 dark:text-amber-400",
};

// ── V1 styles ─────────────────────────────────────────────────────

const CATEGORY_HEADER: Record<NodeCategory, string> = {
  input: "bg-blue-500/10 dark:bg-blue-500/15",
  generation: "bg-[oklch(0.55_0.25_285_/_12%)]",
  transform: "bg-emerald-500/10 dark:bg-emerald-500/15",
  output: "bg-amber-500/10 dark:bg-amber-500/15",
};

// ── V2 styles ─────────────────────────────────────────────────────

// Full-card pastel tint per category
const CATEGORY_BG_V2: Record<NodeCategory, string> = {
  input: "bg-blue-50 dark:bg-[oklch(0.13_0.015_240)]",
  generation: "bg-[oklch(0.975_0.008_285)] dark:bg-[oklch(0.13_0.015_285)]",
  transform: "bg-emerald-50 dark:bg-[oklch(0.13_0.015_160)]",
  output: "bg-amber-50 dark:bg-[oklch(0.13_0.015_80)]",
};

const CATEGORY_BORDER_V2: Record<NodeCategory, string> = {
  input: "border-blue-200/70 dark:border-blue-500/20",
  generation: "border-violet-200/60 dark:border-[oklch(0.55_0.25_285_/_22%)]",
  transform: "border-emerald-200/70 dark:border-emerald-500/20",
  output: "border-amber-200/70 dark:border-amber-500/20",
};

// AI provider badge shown top-right of generation nodes — real brand icons
const PROVIDER_BADGE: Partial<Record<string, React.ReactNode>> = {
  "llm-text": <HugeiconsIcon icon={ClaudeIcon} className="size-[10px]" />,
  "image-gen": (
    <HugeiconsIcon icon={GoogleGeminiIcon} className="size-[10px]" />
  ),
  "audio-tts": (
    <img
      src={elevenlabsImg}
      alt="ElevenLabs"
      className="size-6 object-contain dark:invert"
    />
  ),
  "video-gen": (
    <HugeiconsIcon icon={GoogleGeminiIcon} className="size-[10px]" />
  ),
};

// ─── Edge colors ──────────────────────────────────────────────────

const EDGE_COLORS = {
  string: "oklch(0.55 0.25 285)",
  image: "oklch(0.65 0.18 160)",
  audio: "oklch(0.70 0.15 80)",
};

// Node width as percentage of canvas
const NODE_W = 18;

// ─── Node definitions ────────────────────────────────────────────

const nodes: MockNodeData[] = [
  {
    id: "text-input",
    label: "Text Input",
    category: "input",
    x: 4,
    y: 50,
    content: "text-snippet",
    icon: (
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M4 6h16M4 12h10M4 18h14" />
      </svg>
    ),
  },
  {
    id: "llm-text",
    label: "Claude Sonnet 4.6",
    category: "generation",
    x: 25,
    y: 50,
    content: "bars",
    icon: (
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M12 2a7 7 0 0 1 7 7c0 3-2 5-4 6v2H9v-2c-2-1-4-3-4-6a7 7 0 0 1 7-7z" />
        <path d="M9 21h6" />
      </svg>
    ),
  },
  {
    id: "image-gen",
    label: "Nano Banana 2",
    category: "generation",
    x: 51.5,
    y: 40,
    content: "image",
    icon: (
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    ),
  },
  {
    id: "audio-tts",
    label: "Eleven Labs",
    category: "generation",
    x: 51.5,
    y: 64,
    content: "waveform",
    icon: (
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <path d="M12 19v3" />
      </svg>
    ),
  },
  {
    id: "video-gen",
    label: "Veo 3.1",
    category: "generation",
    x: 78,
    y: 50,
    content: "video",
    icon: (
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M10 9l5 3-5 3V9z" />
      </svg>
    ),
  },
];

const connections: ConnectionData[] = [
  { from: "text-input", to: "llm-text", color: EDGE_COLORS.string },
  { from: "llm-text", to: "image-gen", color: EDGE_COLORS.string },
  { from: "llm-text", to: "audio-tts", color: EDGE_COLORS.string },
  { from: "image-gen", to: "video-gen", color: EDGE_COLORS.string },
  { from: "audio-tts", to: "video-gen", color: EDGE_COLORS.string },
];

// ─── Shared body content ──────────────────────────────────────────

function TextSnippetBody() {
  return (
    <div className="px-2.5 py-2">
      <p className="text-muted-foreground line-clamp-3 text-[7px] leading-[1.5] sm:text-[8px]">
        A steaming cup of pour-over coffee in golden morning light. Slow motion
        pour, cream swirling.
      </p>
    </div>
  );
}

function PlaceholderBars({ count }: { count: number }) {
  return (
    <div className="space-y-1.5 px-2.5 py-2">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="h-[5px] rounded-full bg-black/[0.06] dark:bg-white/[0.06]"
          style={{ width: `${80 - i * 20}%` }}
        />
      ))}
    </div>
  );
}

function ImageBody() {
  return (
    <div className="p-1.5">
      <img
        src={sceneImage}
        alt="AI-generated scene"
        className="aspect-video w-full rounded object-cover"
      />
    </div>
  );
}

function WaveformBody() {
  return (
    <div className="flex h-[42px] items-center justify-center gap-[2.5px] px-2 py-2">
      {Array.from({ length: 32 }).map((_, i) => {
        const minH = 4;
        const maxH = 6 + Math.sin(i * 0.8) * 8 + (i % 3) * 2;
        return (
          <motion.div
            key={i}
            className="w-[2px] shrink-0 rounded-full bg-foreground/30"
            style={{ height: minH }}
            animate={{ height: [minH, maxH, minH * 1.5, maxH * 0.7, minH] }}
            transition={{
              duration: 1.2 + (i % 3) * 0.3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.07,
            }}
          />
        );
      })}
    </div>
  );
}

function VideoBody() {
  return (
    <div className="p-1.5">
      <video
        src={showcaseVideo}
        autoPlay
        loop
        muted
        playsInline
        className="aspect-video w-full rounded object-cover"
      />
    </div>
  );
}

// ─── V2-only: expanded output section for the Claude node ────────
// Replaces skeleton bars with a clear list of Claude's two structured
// outputs, communicating that the pipeline supports multi-output routing.

function OutputPillsExpanded() {
  return (
    <div className="space-y-1 px-2.5 py-2">
      {/* <p className="mb-1.5 text-[7px] font-semibold uppercase tracking-wider text-foreground/35">
        Structured outputs
      </p> */}
      <div className="flex items-center gap-1.5 rounded-md bg-[oklch(0.55_0.25_285_/_8%)] px-2 py-1.5 dark:bg-[oklch(0.55_0.25_285_/_12%)]">
        <svg
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="shrink-0 text-[oklch(0.55_0.25_285)] dark:text-[oklch(0.65_0.22_285)]"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
        <span className="text-[8px] font-medium text-[oklch(0.45_0.22_285)] dark:text-[oklch(0.72_0.18_285)]">
          Image prompt
        </span>
      </div>
      <div className="flex items-center gap-1.5 rounded-md bg-amber-500/[0.08] px-2 py-1.5 dark:bg-amber-500/[0.10]">
        <svg
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="shrink-0 text-amber-600 dark:text-amber-400"
        >
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        </svg>
        <span className="text-[8px] font-medium text-amber-700 dark:text-amber-400">
          Voice script
        </span>
      </div>
    </div>
  );
}

// ─── Mock Node props ──────────────────────────────────────────────

interface MockNodeProps {
  node: MockNodeData;
  index: number;
  isInView: boolean;
}

// ─── Node V1 (original) ───────────────────────────────────────────

function MockNode({ node, index, isInView }: MockNodeProps) {
  const delay = 0.5 + index * 0.15;

  return (
    <motion.div
      className="absolute -translate-y-1/2"
      style={{ left: `${node.x}%`, top: `${node.y}%`, width: `${NODE_W}%` }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ type: "spring", stiffness: 300, damping: 24, delay }}
    >
      <div className="rounded-lg border border-black/8 bg-white shadow-sm dark:border-white/10 dark:bg-[oklch(0.12_0_0)]">
        <div
          className={cn(
            "flex items-center gap-1.5 rounded-t-lg px-2.5 py-1.5",
            CATEGORY_HEADER[node.category],
          )}
        >
          <span className={cn("shrink-0", CATEGORY_ICON[node.category])}>
            {node.icon}
          </span>
          <span className="truncate text-[10px] font-semibold text-foreground/90">
            {node.label}
          </span>
        </div>
        {node.content === "text-snippet" && <TextSnippetBody />}
        {node.content === "bars" && <PlaceholderBars count={3} />}
        {node.content === "image" && <ImageBody />}
        {node.content === "waveform" && <WaveformBody />}
        {node.content === "video" && <VideoBody />}
      </div>
    </motion.div>
  );
}

// ─── Node V2 (redesign) ───────────────────────────────────────────
// Changes vs V1:
// • Full-card pastel tint, no separate header strip
// • Single header row: icon + label left, provider badge right
// • Thin divider between header and body
// • Output pills on Claude node showing its two structured outputs

function MockNodeV2({ node, index, isInView }: MockNodeProps) {
  const delay = 0.5 + index * 0.15;
  const badge = PROVIDER_BADGE[node.id];

  return (
    <motion.div
      className="absolute -translate-y-1/2"
      style={{ left: `${node.x}%`, top: `${node.y}%`, width: `${NODE_W}%` }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ type: "spring", stiffness: 300, damping: 24, delay }}
    >
      <div
        className={cn(
          "rounded-lg border shadow-sm",
          CATEGORY_BG_V2[node.category],
          CATEGORY_BORDER_V2[node.category],
        )}
      >
        {/* Header: icon + label on left, provider badge on right */}
        <div className="flex items-center justify-between gap-1 px-2.5 py-1.5">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className={cn("shrink-0", CATEGORY_ICON[node.category])}>
              {node.icon}
            </span>
            <span className="truncate text-[10px] font-semibold text-foreground/90">
              {node.label}
            </span>
          </div>
          {badge && (
            <div className="flex size-[18px] shrink-0 items-center justify-center rounded bg-black/[0.05] dark:bg-white/[0.07]">
              {badge}
            </div>
          )}
        </div>

        {/* Thin divider */}
        <div className="mx-2.5 h-px bg-foreground/[0.07]" />

        {/* Body */}
        {node.content === "text-snippet" && <TextSnippetBody />}
        {/* Claude node: expanded output list instead of skeleton bars */}
        {node.id === "llm-text" ? (
          <OutputPillsExpanded />
        ) : (
          node.content === "bars" && <PlaceholderBars count={3} />
        )}
        {node.content === "image" && <ImageBody />}
        {node.content === "waveform" && <WaveformBody />}
        {node.content === "video" && <VideoBody />}
      </div>
    </motion.div>
  );
}

// ─── Connection SVG paths ─────────────────────────────────────────

function ConnectionPaths({ isInView }: { isInView: boolean }) {
  const getAnchor = (id: string, side: "left" | "right") => {
    const node = nodes.find((n) => n.id === id);
    if (!node) return { x: 0, y: 0 };
    return { x: side === "right" ? node.x + NODE_W : node.x, y: node.y };
  };

  const DASH = 0.5;
  const GAP = 4;
  const PERIOD = DASH + GAP;

  return (
    <svg
      className="pointer-events-none absolute inset-0 size-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      fill="none"
    >
      {connections.map((conn, i) => {
        const from = getAnchor(conn.from, "right");
        const to = getAnchor(conn.to, "left");
        const cpOffset = Math.abs(to.x - from.x) * 0.45;
        const d = `M ${from.x},${from.y} C ${from.x + cpOffset},${from.y} ${to.x - cpOffset},${to.y} ${to.x},${to.y}`;
        const drawDelay = 1.5 + i * 0.2;
        const flowDelay = drawDelay + 0.85;

        return (
          <g key={`${conn.from}-${conn.to}`}>
            {/* Static track */}
            <motion.path
              d={d}
              stroke={conn.color}
              strokeWidth={0.3}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={isInView ? { pathLength: 1, opacity: 0.3 } : {}}
              transition={{ duration: 0.8, delay: drawDelay, ease: "easeOut" }}
            />
            {/* Flowing dashes */}
            <motion.path
              d={d}
              stroke={conn.color}
              strokeWidth={0.05}
              strokeLinecap="round"
              strokeDasharray={`${DASH} ${GAP}`}
              initial={{ opacity: 0, strokeDashoffset: 0 }}
              animate={
                isInView
                  ? { opacity: 0.75, strokeDashoffset: [0, -PERIOD] }
                  : {}
              }
              transition={{
                opacity: { duration: 0.3, delay: flowDelay },
                strokeDashoffset: {
                  duration: 1.6,
                  repeat: Infinity,
                  ease: "linear",
                  delay: flowDelay,
                },
              }}
            />
          </g>
        );
      })}
    </svg>
  );
}

// ─── Browser chrome ───────────────────────────────────────────────

function BrowserChrome() {
  return (
    <div className="flex justify-between items-center gap-2 border-b border-black/8 bg-white/80 px-4 py-2.5 dark:border-white/8 dark:bg-white/5">
      <div className="flex items-center gap-1.5">
        <div className="size-[9px] rounded-full bg-[#FF5F57]" />
        <div className="size-[9px] rounded-full bg-[#FEBC2E]" />
        <div className="size-[9px] rounded-full bg-[#28C840]" />
      </div>
      <div className="flex w-full items-center gap-1.5 rounded-md bg-black/[0.04] px-3 py-0.5 dark:bg-white/[0.06]">
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 text-muted-foreground/60"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <span className="text-muted-foreground/60 w-full text-center text-[11px] font-medium tracking-wide">
          Rayloop.ai
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="size-[9px] rounded-full bg-black/[0.04] dark:bg-white/[0.06]" />
        <div className="size-[9px] rounded-full bg-black/[0.04] dark:bg-white/[0.06]" />
        <div className="size-[9px] rounded-full bg-black/[0.04] dark:bg-white/[0.06]" />
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────

export function HeroCanvas() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="overflow-hidden rounded-xl border border-black/10 bg-white shadow-2xl shadow-black/10 dark:border-white/10 dark:bg-[oklch(0.10_0_0)] dark:shadow-black/40 [mask-image:linear-gradient(to_bottom,black_75%,transparent_100%)]"
    >
      <BrowserChrome />

      <div className="relative aspect-[16/9] overflow-hidden">
        <DotPattern
          className="text-black/[0.15] dark:text-white/[0.22] [mask-image:radial-gradient(ellipse_at_center,white_50%,transparent_85%)]"
          width={20}
          height={20}
          cr={0.8}
        />

        <ConnectionPaths isInView={isInView} />

        {nodes.map((node, i) =>
          USE_V2_NODES ? (
            <MockNodeV2
              key={node.id}
              node={node}
              index={i}
              isInView={isInView}
            />
          ) : (
            <MockNode key={node.id} node={node} index={i} isInView={isInView} />
          ),
        )}
      </div>
    </motion.div>
  );
}
